/**
 * User Transformation Service
 * 
 * Handles all data transformations between API and form formats.
 * This service ensures consistency across all user management operations.
 * 
 * @module UserTransformationService
 */

import { getDepartmentsByFacilityId } from '../utils/apiClient';

/**
 * Transforms form data (UI format) to API format
 * 
 * @param {Object} formData - Form data with facilityDepartments structure
 * @returns {Object} API formatted data with flat arrays
 */
export const transformFormToAPI = (formData) => {
  // Admin users don't have facility/department assignments
  if (formData.role === 'admin') {
    return {
      ...formData,
      facilityIds: [],
      departmentIds: [],
      facilityDepartments: undefined, // Remove UI-only field
    };
  }
  
  const facilityIds = formData.facilityDepartments
    ?.filter(fd => fd.facilityId)
    ?.flatMap(fd => {
      // Handle both single facility IDs (users) and arrays (supervisors)
      if (Array.isArray(fd.facilityId)) {
        return fd.facilityId;
      } else {
        return [fd.facilityId];
      }
    }) || [];
  
  const departmentIds = formData.facilityDepartments
    ?.filter(fd => fd.departmentIds && fd.departmentIds.length > 0)
    ?.flatMap(fd => fd.departmentIds) || [];

  return {
    ...formData,
    facilityIds: [...new Set(facilityIds)], // Remove duplicates
    departmentIds: [...new Set(departmentIds)], // Remove duplicates
    facilityDepartments: undefined, // Remove UI-only field
  };
};

/**
 * Transforms API data to form format for editing
 * 
 * @param {Object} userData - User data from API
 * @param {Function} setFacilitiesDepartments - State setter for departments cache
 * @returns {Promise<Object>} Form formatted data with facilityDepartments structure
 */
export const transformAPIToForm = async (userData, setFacilitiesDepartments) => {
  // Handle null values - convert to empty arrays
  const facilityIds = userData.facilityIds || [];
  const departmentIds = userData.departmentIds || [];
  
  // Admin users don't need facility/department processing
  if (userData.role === 'admin') {
    return {
      ...userData,
      facilityDepartments: [{ facilityId: '', departmentIds: [] }],
    };
  }
  
  if (facilityIds.length === 0) {
    return {
      ...userData,
      facilityDepartments: [{ facilityId: '', departmentIds: [] }],
    };
  }

  try {
    // Fetch departments for each facility to map them correctly
    const facilityDepartmentsMap = {};
    
    for (const facilityId of facilityIds) {
      try {
        const response = await getDepartmentsByFacilityId(facilityId);
        if (response.statusCode === 'SUCCESS' && Array.isArray(response.response)) {
          facilityDepartmentsMap[facilityId] = response.response;
        }
      } catch (error) {
        console.warn(`Failed to fetch departments for facility ${facilityId}:`, error);
        facilityDepartmentsMap[facilityId] = [];
      }
    }

    // Map departmentIds back to their respective facilities
    // For supervisors: combine all facilities into single entry with array of facility IDs
    // For users: create separate entries for each facility
    let facilityDepartments;
    
    if (userData.role === 'supervisor') {
      // Supervisors get all facility IDs in a single entry as an array
      facilityDepartments = [{
        facilityId: facilityIds, // Array of facility IDs for multi-select
        departmentIds: [] // Supervisors don't assign departments
      }];
    } else {
      // Users get separate entries for each facility
      facilityDepartments = facilityIds.map(facilityId => {
        const facilityDepts = facilityDepartmentsMap[facilityId] || [];
        
        // Filter out inactive departments when loading user data
        const activeDeptIds = departmentIds.filter(deptId => {
          const dept = facilityDepts.find(d => d._id === deptId);
          // Only include if department exists and is active
          return dept && dept.isActive !== false;
        });
        
        return {
          facilityId,
          departmentIds: activeDeptIds,
        };
      });
    }

    // Update departments cache
    if (setFacilitiesDepartments) {
      setFacilitiesDepartments(prev => ({
        ...prev,
        ...facilityDepartmentsMap
      }));
    }

    // Filter departmentIds to only include active departments
    const activeDepartmentIds = departmentIds.filter(deptId => {
      // Check all facilities to see if this department exists and is active
      for (const facilityId of facilityIds) {
        const facilityDepts = facilityDepartmentsMap[facilityId] || [];
        const dept = facilityDepts.find(d => d._id === deptId);
        if (dept && dept.isActive !== false) {
          return true;
        }
      }
      return false;
    });

    return {
      ...userData,
      // CRITICAL: Ensure departmentIds is NEVER null (breaks form validation)
      // Filter out inactive departments
      departmentIds: activeDepartmentIds || [],
      facilityIds: facilityIds || [],
      facilityDepartments: facilityDepartments.length > 0 ? facilityDepartments : [{ facilityId: '', departmentIds: [] }],
    };
  } catch (error) {
    console.error('Error transforming API data for form:', error);
    return {
      ...userData,
      // CRITICAL: Ensure departmentIds is NEVER null
      departmentIds: [],
      facilityIds: [],
      facilityDepartments: [{ facilityId: '', departmentIds: [] }],
    };
  }
};

/**
 * Default service export
 */
const UserTransformationService = {
  transformFormToAPI,
  transformAPIToForm
};

export default UserTransformationService;